export type ServerKind = "http" | "mongo" | "postgres";

export type RequestType = "graphql" | "rest" | "mongo" | "sql";

export type CatalogKind = "graphql-schema" | "mongo-catalog" | "postgres-catalog";

export type MongoOperation = "find" | "findOne" | "aggregate" | "replaceById";

export interface HttpConnectionConfig {
  kind: "http";
  baseUrl: string;
  graphqlPath?: string;
  restPath?: string;
}

export interface MongoConnectionConfig {
  kind: "mongo";
  connectionUri: string;
  database: string;
}

export interface PostgresConnectionConfig {
  kind: "postgres";
  connectionUri: string;
  defaultDatabase?: string | undefined;
  defaultSchema?: string | undefined;
}

export type ServerConnectionConfig =
  | HttpConnectionConfig
  | MongoConnectionConfig
  | PostgresConnectionConfig;

export interface MongoOperationConfig {
  kind: "mongo";
  collection: string;
  operation: MongoOperation;
}

export interface SqlOperationConfig {
  kind: "sql";
  defaultSchema?: string | undefined;
}

export type QueryOperationConfig = MongoOperationConfig | SqlOperationConfig;

export function normalizeServerKind(input: unknown): ServerKind {
  return input === "mongo" || input === "postgres" ? input : "http";
}

export function normalizeRequestType(input: unknown): RequestType {
  return input === "rest" || input === "mongo" || input === "sql" ? input : "graphql";
}

export function catalogKindForServerKind(serverKind: ServerKind): CatalogKind {
  switch (serverKind) {
    case "mongo":
      return "mongo-catalog";
    case "postgres":
      return "postgres-catalog";
    default:
      return "graphql-schema";
  }
}

export function defaultServerConnectionConfig(input: {
  serverKind?: unknown;
  connectionConfig?: unknown;
  baseUrl?: string | null | undefined;
  graphqlPath?: string | null | undefined;
  restPath?: string | null | undefined;
}): ServerConnectionConfig {
  const serverKind = normalizeServerKind(input.serverKind);
  const config = input.connectionConfig as Partial<ServerConnectionConfig> | null | undefined;

  if (serverKind === "mongo") {
    return {
      kind: "mongo",
      connectionUri: typeof config?.kind === "string" && config.kind === "mongo" && typeof (config as MongoConnectionConfig).connectionUri === "string"
        ? (config as MongoConnectionConfig).connectionUri
        : "",
      database: typeof config?.kind === "string" && config.kind === "mongo" && typeof (config as MongoConnectionConfig).database === "string"
        ? (config as MongoConnectionConfig).database
        : "",
    };
  }

  if (serverKind === "postgres") {
    const pg = config?.kind === "postgres" ? config as PostgresConnectionConfig : null;
    return {
      kind: "postgres",
      connectionUri: pg?.connectionUri ?? "",
      ...(pg?.defaultDatabase ? { defaultDatabase: pg.defaultDatabase } : {}),
      ...(pg?.defaultSchema ? { defaultSchema: pg.defaultSchema } : {}),
    };
  }

  const http = config?.kind === "http" ? config as HttpConnectionConfig : null;
  return {
    kind: "http",
    baseUrl: http?.baseUrl ?? input.baseUrl ?? "",
    ...(http?.graphqlPath ?? input.graphqlPath ? { graphqlPath: http?.graphqlPath ?? input.graphqlPath ?? "" } : {}),
    ...(http?.restPath ?? input.restPath ? { restPath: http?.restPath ?? input.restPath ?? "" } : {}),
  };
}

export function defaultQueryOperationConfig(input: {
  requestType?: unknown;
  operationConfig?: unknown;
}): QueryOperationConfig | undefined {
  const requestType = normalizeRequestType(input.requestType);
  const config = input.operationConfig as Partial<QueryOperationConfig> | null | undefined;

  if (requestType === "mongo") {
    const mongo = config?.kind === "mongo" ? config as MongoOperationConfig : null;
    return {
      kind: "mongo",
      collection: mongo?.collection ?? "",
      operation: mongo?.operation ?? "find",
    };
  }

  if (requestType === "sql") {
    const sql = config?.kind === "sql" ? config as SqlOperationConfig : null;
    return {
      kind: "sql",
      ...(sql?.defaultSchema ? { defaultSchema: sql.defaultSchema } : {}),
    };
  }

  return undefined;
}

export function isHttpServerKind(serverKind: unknown): boolean {
  return normalizeServerKind(serverKind) === "http";
}

export function isDatabaseRequestType(requestType: unknown): boolean {
  const normalized = normalizeRequestType(requestType);
  return normalized === "mongo" || normalized === "sql";
}
